// حارس الشاشة — خطأ في الرسم يُعرَض ولا يترك صفحةً بيضاء.
//
// كان أي خطأ برمجي أثناء رسم أي شاشة يُسقط شجرة React كلّها، فيرى المستخدم **صفحة بيضاء**
// بلا رسالة ولا زرّ ولا تفسير — ولا يعرف أن عليه التحديث، ولا يعرف من يُبلّغ. وهذا أسوأ ما
// يمكن أن تفعله شاشة: أن تختفي بلا أن تقول شيئًا.
//
// والحارس يمسك الخطأ عند حدّه: التبويب المكسور وحده يُستبدَل برسالة وزرَّي «إعادة المحاولة»
// و«تحديث الصفحة»، والترويسة وبقيّة النظام تبقى كما هي. و`resetKey` يعيد المحاولة تلقائيًا
// عند الانتقال إلى تبويبٍ آخر، فلا يبقى الخطأ عالقًا بعد أن تركه المستخدم.
//
// **ولا يُعرض نصّ الخطأ إلا خلف زرّ**: رسالة المتصفّح إنجليزية تقنية تُخيف من يقرؤها، لكنها
// نافعة لمن يُبلّغ — فتُخبّأ وتُتاح.
import { Component, type ErrorInfo, type ReactNode } from 'react';

interface Props { children: ReactNode; resetKey?: string;
  /** الحارس الأعلى حول البرنامج كلّه (06/10/2026): الترويسة والجرس نفسهما سقطا — فالرسالة تقول «البرنامج» لا «هذه الشاشة» */
  whole?: boolean }
interface State { error: Error | null; details: string | null }

export class ErrorBoundary extends Component<Props, State> {
  state: State = { error: null, details: null };

  static getDerivedStateFromError(error: Error): Partial<State> {
    return { error };
  }

  componentDidCatch(error: Error, info: ErrorInfo): void {
    // يبقى في سجل المتصفّح لمن يفتحه، ولا يُرسل إلى أي خدمة خارجية (لا تتبّع ولا تقارير)
    console.error('[ErrorBoundary]', error, info.componentStack);
    this.setState({ details: `${error.message}\n${info.componentStack ?? ''}`.slice(0, 2000) });
  }

  componentDidUpdate(prev: Props): void {
    // تبويبٌ جديد = محاولةٌ جديدة: لا يبقى الخطأ معلّقًا على شاشةٍ لم يعد فيها
    if (this.state.error && prev.resetKey !== this.props.resetKey) this.setState({ error: null, details: null });
  }

  render(): ReactNode {
    const { error, details } = this.state;
    if (!error) return this.props.children;

    return (
      <div className="card mx-auto my-8 max-w-2xl p-6 text-center">
        <h2 className="text-lg font-bold text-rose-700">{this.props.whole ? 'تعذّر عرض البرنامج' : 'تعذّر عرض هذه الشاشة'}</h2>
        <p className="mt-2 text-sm leading-7 text-ink-700">
          {this.props.whole
            ? 'حصل خطأ غير متوقّع — ولا شيء من بياناتك ضاع. اضغط «تحديث الصفحة»، وإن تكرّر أبلغ الإدارة بما كنت تفعله لحظتها.'
            : 'حصل خطأ غير متوقّع في هذه الشاشة وحدها — بقيّة النظام تعمل، ولا شيء من بياناتك ضاع. جرّب مرة أخرى، وإن تكرّر أبلغ الإدارة بما كنت تفعله لحظتها.'}
        </p>
        <div className="mt-4 flex flex-wrap justify-center gap-2">
          <button type="button" className="btn-primary px-5 py-2"
            onClick={() => this.setState({ error: null, details: null })}>
            إعادة المحاولة
          </button>
          <button type="button" className="btn-ghost px-5 py-2" onClick={() => window.location.reload()}>
            تحديث الصفحة
          </button>
        </div>
        {details && (
          <details className="mt-4 text-start">
            <summary className="cursor-pointer text-xs text-ink-500">تفاصيل تقنية (للإبلاغ)</summary>
            <pre dir="ltr" className="mt-2 max-h-48 overflow-auto whitespace-pre-wrap break-all rounded-lg bg-slate-50 p-3 text-xs text-ink-600">
              {details}
            </pre>
          </details>
        )}
      </div>
    );
  }
}
